import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
  Stack,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  resetProductState,
  sellerProductsForProfile,
  SORT_BY,
  deleteProduct,
} from "../../redux/actions/productActions";
import { Product } from "../../redux/types/productTypes";
import { RootState } from "../../redux/store";
import { ResultsWithFilter } from "../product/ResultsWithFilter";
import { CenteredLoading } from "../common/CenteredLoading";
import { convertTimestampToDate } from "../../utils";
import { PopupDialog } from "./PopupDialog";
import SnackbarAlert, { SnackbarRef } from "../common/SnackbarAlert";
import { useStripeAccountStatus } from "../../hooks/useStripeAccountStatus";
import { createStripeAccountLink } from "../../pages/Seller/seller-setup-api";

const PAGE_SIZE = 12;

interface IMyListingProps {
  profileId: string | number | null;
  defaultFilter: SORT_BY;
}

export const MyListing: React.FC<IMyListingProps> = ({
  profileId,
  defaultFilter,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const snackbarRef = useRef<SnackbarRef>(null);

  const {
    loading: stripeStatusLoading,
    isVerificationPending,
    needsAction,
    sellRoute,
    isApproved,
    secondsUntilNextCheck,
  } = useStripeAccountStatus();
  const [verificationLinkLoading, setVerificationLinkLoading] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<SORT_BY>(defaultFilter);
  const [lastDocId, setLastDocId] = useState<string | null>(null);
  const [isLoadingFromScroll, setIsLoadingFromScroll] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasAnyProducts, setHasAnyProducts] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [forceReplace, setForceReplace] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);
  // Guards against briefly rendering stale products left in the shared
  // redux `products` slice by other pages (e.g. the marketplace feed)
  // before this component's own scoped fetch has actually started.
  const sawOwnLoadingRef = useRef(false);

  const { loading, productsData } = useSelector(
    (state: RootState) => state.products,
  );
  const { products } = productsData;

  // Reset when profileId changes
  useEffect(() => {
    setHasAnyProducts(false);
    sawOwnLoadingRef.current = false;
  }, [profileId]);

  // Initial load
  useEffect(() => {
    dispatch(resetProductState());
    setAllProducts([]);
    setLastDocId(null);
    dispatch(
      sellerProductsForProfile(
        Number(profileId),
        "",
        selectedFilter,
        PAGE_SIZE,
      ),
    );
  }, [dispatch, profileId, selectedFilter]);

  // Refetch after delete
  useEffect(() => {
    if (shouldRefetch && profileId) {
      // Clear local state first to force replacement
      setAllProducts([]);
      setLastDocId(null);
      // Set flag to force replacement when new products arrive
      setForceReplace(true);
      // Reset Redux state and fetch fresh data
      dispatch(resetProductState());
      dispatch(
        sellerProductsForProfile(
          Number(profileId),
          "",
          selectedFilter,
          PAGE_SIZE,
        ),
      );
      setShouldRefetch(false);
    }
  }, [shouldRefetch, dispatch, profileId, selectedFilter]);

  // Append new results - replace on reset, append on scroll
  useEffect(() => {
    if (loading) {
      sawOwnLoadingRef.current = true;
    }

    // Until this component's own scoped fetch has started at least once,
    // `products`/`loading` may still reflect a stale, unrelated fetch from
    // another page (e.g. the marketplace feed), which is not scoped by
    // ownerId. Ignore it rather than briefly rendering someone else's item.
    if (!sawOwnLoadingRef.current) {
      return;
    }

    if (!loading && products.length > 0) {
      setAllProducts((prevProducts) => {
        // If prevProducts is empty, lastDocId is null, or forceReplace is true, this is a fresh load - replace instead of append
        const shouldReplace =
          forceReplace || prevProducts.length === 0 || lastDocId === null;

        let unique: Product[];
        if (shouldReplace) {
          // On fresh load, take only the first PAGE_SIZE items and ensure no duplicates
          unique = products
            .slice(0, PAGE_SIZE)
            .filter(
              (item, index, self) =>
                index ===
                self.findIndex((i) => i.documentId === item.documentId),
            );
        } else {
          // On scroll, append new products
          unique = [...prevProducts, ...products].filter(
            (item, index, self) =>
              index === self.findIndex((i) => i.documentId === item.documentId),
          );
        }

        const last = unique[unique.length - 1];
        if (last?.documentId) setLastDocId(last.documentId);

        return unique;
      });

      // Reset forceReplace flag after replacement
      if (forceReplace) {
        setForceReplace(false);
      }

      // Track if user has any products (from non-INACTIVE filters)
      if (selectedFilter !== SORT_BY.INACTIVE) {
        setHasAnyProducts(true);
      }

      // Reset loading flags
      setIsLoadingFromScroll(false);
      setIsFilterChanging(false);
    } else if (!loading && products.length === 0) {
      // Reset filter changing flag when loading completes with no results
      setIsFilterChanging(false);

      // Only clear products when explicitly requested (e.g., after deletion with forceReplace)
      if (forceReplace) {
        setAllProducts([]);
        setLastDocId(null);
        setForceReplace(false);
      }
    }
  }, [products, loading, selectedFilter, lastDocId, forceReplace]);

  // Track if user has products based on totalRecords from non-INACTIVE filters
  useEffect(() => {
    if (
      !loading &&
      selectedFilter !== SORT_BY.INACTIVE &&
      productsData.totalRecords > 0
    ) {
      setHasAnyProducts(true);
    }
  }, [loading, selectedFilter, productsData.totalRecords]);

  // Infinite scroll logic
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    if (
      scrollTop + clientHeight >= scrollHeight - 100 &&
      !loading &&
      !isLoadingFromScroll &&
      lastDocId !== null &&
      allProducts.length > 0 // Only trigger if we have items to scroll through
    ) {
      setIsLoadingFromScroll(true);
      dispatch(
        sellerProductsForProfile(
          Number(profileId),
          lastDocId || "",
          selectedFilter,
          PAGE_SIZE,
        ),
      );
    }
  }, [
    dispatch,
    profileId,
    lastDocId,
    selectedFilter,
    loading,
    isLoadingFromScroll,
    allProducts.length,
  ]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const filterHandler = (value: string) => {
    const selected = value as SORT_BY;
    setSelectedFilter(selected);
    setAllProducts([]);
    setLastDocId(null);
    setIsFilterChanging(true);
    dispatch(
      sellerProductsForProfile(Number(profileId), "", selected, PAGE_SIZE),
    );
  };

  const handleProductClick = (productUrl: string) => {
    window.open(productUrl, "_blank");
  };

  // Account Links are single-use and expire quickly, so generate a fresh one on click.
  const handleCompleteVerificationOnStripe = async () => {
    if (!profileId || verificationLinkLoading) return;
    setVerificationLinkLoading(true);
    try {
      // ?refresh=true tells useStripeAccountStatus this is a fresh return from fixing
      // things on Stripe's hosted page, so a stuck ACTION_REQUIRED account gets
      // re-evaluated instead of showing the stale message until full approval.
      const returnUrl = `${window.location.origin}/account?refresh=true`;
      const { url } = await createStripeAccountLink(
        Number(profileId),
        returnUrl,
      );
      window.location.href = url;
    } catch (e) {
      setVerificationLinkLoading(false);
      snackbarRef.current?.showSnackbar(
        e instanceof Error
          ? e.message
          : "Failed to open verification page. Please try again.",
        "error",
      );
    }
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);
    try {
      // Call the delete product action
      await dispatch(deleteProduct(productToDelete) as any);

      // Close dialog first
      setDeleteDialogOpen(false);
      setProductToDelete(null);

      // Show success snackbar
      snackbarRef.current?.showSnackbar(
        "Your listing has been deleted",
        "success",
      );

      // Clear local state to force a fresh load
      setAllProducts([]);
      setLastDocId(null);

      // Trigger refetch after a short delay to ensure backend processed the deletion
      setTimeout(() => {
        setShouldRefetch(true);
      }, 500);
    } catch (error) {
      console.error("Failed to delete product:", error);
      snackbarRef.current?.showSnackbar(
        "Failed to delete product. Please try again.",
        "error",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  if (
    !isLoadingFromScroll &&
    (loading || isFilterChanging) &&
    allProducts.length === 0
  ) {
    return <CenteredLoading />;
  }

  return (
    <Box>
      <SnackbarAlert ref={snackbarRef} />
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <Typography variant="h6" fontWeight="bold">
            My Listings
          </Typography>
        </Grid>
        <Grid item>
          <Grid container alignItems="center" spacing={1} wrap="nowrap">
            <ResultsWithFilter
              menuItems={[
                { label: "Filter by:", value: "", isDisabled: true },
                { label: "Latest", value: SORT_BY.LATEST },
                { label: "Popular", value: SORT_BY.POPULAR },
                { label: "Active", value: SORT_BY.ACTIVE },
                { label: "Inactive", value: SORT_BY.INACTIVE },
              ]}
              filterHandler={filterHandler}
              defaultValue={selectedFilter}
              totalResults={productsData.totalRecords}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {allProducts.length > 0 ? (
          allProducts.map((item, index) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
              <Card
                variant="outlined"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0px 8px 20px rgba(0,0,0,0.18)",
                    border: "1px solid transparent",
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={item.productImage || "/placeholder.jpg"}
                  alt={item.productName}
                  sx={{
                    width: "100%",
                    height: 165,
                    objectFit: "contain",
                    background: "#f9f9f9",
                    p: 1,
                  }}
                  onClick={() => handleProductClick(item.productUrl)}
                />

                <CardContent
                  sx={{
                    p: 1.6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.6,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    noWrap
                    sx={{
                      fontSize: "0.9rem",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => handleProductClick(item.productUrl)}
                  >
                    {item.productName || "Product Title"}
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#E78758" }}
                  >
                    {item.productPrice || "0.00"}
                  </Typography>

                  <Stack
                    spacing={0.3}
                    sx={{
                      fontSize: "0.72rem",
                      opacity: 0.85,
                    }}
                  >
                    <Typography variant="caption">
                      👁️ {item.views ?? 0} views
                    </Typography>
                    <Typography variant="caption">
                      📦 {item.productQuantity > 0 ? "Active" : "Inactive"}
                    </Typography>
                    <Typography variant="caption">
                      🛒 Qty: {item.productQuantity ?? 0}
                    </Typography>
                    <Typography variant="caption">
                      📅 {convertTimestampToDate(item.createdTime)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    mt={1}
                  >
                    <Tooltip title="Edit">
                      <IconButton size="small">
                        <EditOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(item)}
                      >
                        <DeleteOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleProductClick(item.productUrl)}
                      >
                        <VisibilityOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              gap={2}
              sx={{ color: "text.secondary" }}
            >
              {selectedFilter === SORT_BY.INACTIVE && hasAnyProducts ? (
                <Typography variant="h6" textAlign="center">
                  You have no inactive any products.
                </Typography>
              ) : stripeStatusLoading ? (
                <CircularProgress size={24} />
              ) : isVerificationPending ? (
                <Alert
                  severity="info"
                  icon={<ScheduleIcon />}
                  sx={{ maxWidth: 480 }}
                >
                  We're waiting for your seller account to be verified. You'll
                  receive an email once you're approved and ready to list items,
                  or if anything else is needed.
                  {secondsUntilNextCheck !== null && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 0.5, opacity: 0.75 }}
                    >
                      Checking again in {secondsUntilNextCheck}s…
                    </Typography>
                  )}
                </Alert>
              ) : needsAction ? (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ maxWidth: 480 }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    Seller Verification Required
                  </Typography>
                  <Typography variant="body2">
                    Stripe needs more information to verify your seller account.
                    Complete the verification to start receiving payouts.{" "}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleCompleteVerificationOnStripe}
                      sx={{
                        cursor: verificationLinkLoading ? "default" : "pointer",
                      }}
                    >
                      {verificationLinkLoading
                        ? "Opening…"
                        : "Complete verification on Stripe"}
                    </Link>
                  </Typography>
                </Alert>
              ) : (
                <>
                  <Typography variant="h6" textAlign="center">
                    You haven't listed anything yet
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="center"
                    sx={{ color: "text.secondary", maxWidth: 480 }}
                  >
                    Start by adding your first toy, collectible, or childhood
                    find.{" "}
                    {isApproved
                      ? ""
                      : `We'll walk you through the quick pre-sell setup before
                    your item goes live.`}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      mt: 3,
                      borderRadius: "20px",
                      width: "200px",
                      color: "white",
                      lineHeight: "unset",
                    }}
                    onClick={() => navigate(sellRoute)}
                  >
                    List Your First Item
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      {loading && allProducts.length >= PAGE_SIZE && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <PopupDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        message={
          <Typography>
            Are you sure you want to delete{" "}
            <b>{productToDelete?.productName}</b>? This action will remove the
            product from your listings.
          </Typography>
        }
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="Delete Product"
      />
    </Box>
  );
};
